import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  BulkReferenceFitPlacement,
  BulkReferencePlacementSummary,
} from '@/api/bulk';
import {
  addFilesToRows,
  computeStatusCounts,
  hasPendingOrFailedCells,
  moveBackInRows,
  removeCellFromRows,
  removeRowFromRows,
  setRowTitle,
  updateCellInRows,
  type AddFilesResult,
  type BulkFileInput,
  type BulkRow,
  type BulkStatusCounts,
  type BulkUploadedAsset,
  type BulkZone,
  type MoveDirection,
} from '@/lib/bulk/pairing';

export type {
  AddFilesResult,
  BulkCell,
  BulkCellState,
  BulkFileInput,
  BulkRow,
  BulkStatusCounts,
  BulkUploadedAsset,
  BulkZone,
  MoveDirection,
} from '@/lib/bulk/pairing';

/**
 * State of the Bulk Creation flow: step, reference, product selection, motif
 * rows, draft option.
 *
 * Deliberately not persisted (R4, R58): uploads and the selection belong to
 * one flow instance, and a restored selection would silently point at a
 * reference that may have changed since (cf. docs/solutions/ui-bugs/
 * wrong-product-mockup-edit-placement.md). The store holds cell state only;
 * the uploads themselves run in the motifs step, which reports back through
 * `markCellReady` / `markCellError`.
 */

export type BulkStep = 'reference' | 'motifs' | 'create';

export const BULK_STEPS: BulkStep[] = ['reference', 'motifs', 'create'];

/** One colour a reference product is offered in. */
export interface BulkReferenceProductColor {
  productColorId: string;
  /** Localised colour name. */
  name: string;
  hexCode: string | null;
}

/**
 * Purchase (seller cost), selling price and margin of a reference product
 * in euros — one price per product; cost and margin are null when the
 * seller cost is not maintained.
 */
export interface BulkReferenceProductPricing {
  /** Seller cost per piece; null when a cost is not maintained. */
  purchase: number | null;
  selling: number;
  /** selling − purchase; null when the seller cost is unknown. */
  margin: number | null;
}

/** A product of the selected reference. */
export interface BulkReferenceProduct {
  productId: string;
  name: string;
  /** The reference designs a back on this product (R48). */
  hasBack: boolean;
  /** Why the product cannot be selected (e.g. no active colour left); null if selectable. */
  disabledReason: string | null;
  /** Active colours of the reference, capped by the API. */
  colors?: BulkReferenceProductColor[];
  /** Authoritative number of active colours, also beyond the capped list. */
  activeColorCount?: number;
  /** EK/VK/Marge of the reference; null when no price is stored. */
  pricing?: BulkReferenceProductPricing | null;
}

/**
 * One stored placement of the reference as the API sends it (placementMm on
 * its area in mm), for the "will be scaled down" hint (R22) computed with
 * `fitToReference`.
 */
export type BulkReferencePlacement = BulkReferenceFitPlacement;

/** The reference placement per side; a side without a usable row is absent. */
export interface BulkReferencePlacements {
  front?: BulkReferencePlacement;
  back?: BulkReferencePlacement;
}

/** The reference listing as step 01 selected it. */
export interface BulkReferenceSelection {
  id: string;
  name: string;
  products: BulkReferenceProduct[];
  referencePlacement?: BulkReferencePlacements;
  /** Localised method names of the card, for the step 03 summary (R27). */
  methodNames?: string[];
  /** Master placement summary of the card, for the step 03 summary (R27). */
  placement?: BulkReferencePlacementSummary | null;
}

interface BulkCreationData {
  currentStep: BulkStep;
  reference: BulkReferenceSelection | null;
  activeProductIds: string[];
  rows: BulkRow[];
  asDraft: boolean;
}

interface BulkCreationState extends BulkCreationData {
  goToStep: (step: BulkStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  /** Whether the step's CTA may proceed; never gates the pills (R3). */
  isStepComplete: (step: BulkStep) => boolean;

  /** Select a reference and activate all its selectable products; null clears it. */
  selectReference: (reference: BulkReferenceSelection | null) => void;
  /** Toggle a selectable product of the current reference; others are ignored. */
  toggleProduct: (productId: string) => void;
  setAsDraft: (asDraft: boolean) => void;

  addFiles: <F extends BulkFileInput>(
    zone: BulkZone,
    files: readonly F[]
  ) => AddFilesResult<F>;
  markCellReady: (cellId: string, asset: BulkUploadedAsset) => void;
  markCellError: (cellId: string, errorCode: string) => void;
  removeCell: (rowId: string, zone: BulkZone) => void;
  removeRow: (rowId: string) => void;
  moveBack: (rowId: string, direction: MoveDirection) => void;
  setTitle: (rowId: string, title: string) => void;

  reset: () => void;
}

const initialState: BulkCreationData = {
  currentStep: 'reference',
  reference: null,
  activeProductIds: [],
  rows: [],
  // Drafts are the safe default: they need no linked shop account and the
  // seller publishes them later. Switching it off is the deliberate step.
  asDraft: true,
};

/** Active products of the selected reference, in reference order. */
export function selectActiveProducts(
  state: Pick<BulkCreationData, 'reference' | 'activeProductIds'>
): BulkReferenceProduct[] {
  if (!state.reference) return [];
  const active = new Set(state.activeProductIds);
  return state.reference.products.filter(
    (product) =>
      product.disabledReason === null && active.has(product.productId)
  );
}

/** Status line, footer and warning counts for the current rows. */
export function selectStatusCounts(
  state: Pick<BulkCreationData, 'reference' | 'activeProductIds' | 'rows'>
): BulkStatusCounts {
  return computeStatusCounts(state.rows, selectActiveProducts(state));
}

/**
 * Completeness per step, derived from the data rather than stored flags:
 * - reference: a reference is selected and at least one product is active
 * - motifs: at least one row yields a design, and no cell is uploading or failed
 * - create: reference and motifs are both complete
 */
export function isBulkStepComplete(
  step: BulkStep,
  state: Pick<BulkCreationData, 'reference' | 'activeProductIds' | 'rows'>
): boolean {
  switch (step) {
    case 'reference':
      return state.reference !== null && selectActiveProducts(state).length > 0;
    case 'motifs':
      return (
        selectStatusCounts(state).designCount > 0 &&
        !hasPendingOrFailedCells(state.rows)
      );
    case 'create':
      return (
        isBulkStepComplete('reference', state) &&
        isBulkStepComplete('motifs', state)
      );
  }
}

export const useBulkCreationStore = create<BulkCreationState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      goToStep: (step) =>
        set({ currentStep: step }, false, 'bulkCreation/goToStep'),

      goToNextStep: () => {
        const next = BULK_STEPS[BULK_STEPS.indexOf(get().currentStep) + 1];
        if (next) get().goToStep(next);
      },

      goToPreviousStep: () => {
        const previous = BULK_STEPS[BULK_STEPS.indexOf(get().currentStep) - 1];
        if (previous) get().goToStep(previous);
      },

      isStepComplete: (step) => isBulkStepComplete(step, get()),

      selectReference: (reference) =>
        set(
          {
            reference,
            activeProductIds: reference
              ? reference.products
                  .filter((product) => product.disabledReason === null)
                  .map((product) => product.productId)
              : [],
          },
          false,
          'bulkCreation/selectReference'
        ),

      toggleProduct: (productId) => {
        const { reference, activeProductIds } = get();
        const product = reference?.products.find(
          (p) => p.productId === productId
        );
        if (!product || product.disabledReason !== null) return;
        set(
          {
            activeProductIds: activeProductIds.includes(productId)
              ? activeProductIds.filter((id) => id !== productId)
              : [...activeProductIds, productId],
          },
          false,
          'bulkCreation/toggleProduct'
        );
      },

      setAsDraft: (asDraft) =>
        set({ asDraft }, false, 'bulkCreation/setAsDraft'),

      addFiles: (zone, files) => {
        const result = addFilesToRows(get().rows, zone, files);
        if (result.added.length > 0) {
          set({ rows: result.rows }, false, 'bulkCreation/addFiles');
        }
        return result;
      },

      markCellReady: (cellId, asset) =>
        set(
          {
            rows: updateCellInRows(get().rows, cellId, (cell) => ({
              ...cell,
              ...asset,
              state: 'ready',
              errorCode: undefined,
            })),
          },
          false,
          'bulkCreation/markCellReady'
        ),

      markCellError: (cellId, errorCode) =>
        set(
          {
            rows: updateCellInRows(get().rows, cellId, (cell) => ({
              ...cell,
              state: 'error',
              errorCode,
            })),
          },
          false,
          'bulkCreation/markCellError'
        ),

      removeCell: (rowId, zone) =>
        set(
          { rows: removeCellFromRows(get().rows, rowId, zone) },
          false,
          'bulkCreation/removeCell'
        ),

      removeRow: (rowId) =>
        set(
          { rows: removeRowFromRows(get().rows, rowId) },
          false,
          'bulkCreation/removeRow'
        ),

      moveBack: (rowId, direction) =>
        set(
          { rows: moveBackInRows(get().rows, rowId, direction) },
          false,
          'bulkCreation/moveBack'
        ),

      setTitle: (rowId, title) =>
        set(
          { rows: setRowTitle(get().rows, rowId, title) },
          false,
          'bulkCreation/setTitle'
        ),

      reset: () => set(initialState, false, 'bulkCreation/reset'),
    }),
    { name: 'BulkCreationStore' }
  )
);
